import React from 'react';
import { DollarSign, Repeat, Award, CheckCircle2, AlertTriangle, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { MetricData } from '../../types';
import { useCurrency } from '../CurrencyContext';
import { formatValue } from '../../constants';

interface DashboardMetricsProps {
  metrics: MetricData;
}

const DashboardMetrics: React.FC<DashboardMetricsProps> = ({ metrics }) => {
  const { currencyCode } = useCurrency();

  const momGrowthVal = metrics.momGrowth !== undefined ? metrics.momGrowth : (metrics.revenueGrowth || 0);
  const nrrVal = metrics.nrr !== undefined ? metrics.nrr : 100;
  const churnRateVal = metrics.churnRate !== undefined ? metrics.churnRate : 0;
  const ltvVal = (metrics as any).ltv || (metrics.arpu ? metrics.arpu * 12 : (metrics.revenue && metrics.totalCustomers ? Math.round(metrics.revenue / metrics.totalCustomers) : 0));
  const recoveredVal = metrics.recoveredRevenue || 0;

  const cards = [
    {
      title: 'Monthly Recurring Revenue',
      shortLabel: 'MRR',
      value: formatValue(metrics.mrr || 0, currencyCode),
      trend: `${momGrowthVal >= 0 ? '+' : ''}${momGrowthVal.toFixed(1)}%`,
      isPositive: momGrowthVal >= 0,
      subtext: 'vs previous month',
      icon: DollarSign,
      iconBg: 'bg-indigo-50 text-indigo-600 dark:bg-indigo-950 dark:text-indigo-400',
    },
    {
      title: 'Net Revenue Retention',
      shortLabel: 'NRR',
      value: `${nrrVal.toFixed(1)}%`,
      trend: nrrVal >= 100 ? `+${(nrrVal - 100).toFixed(1)}%` : `${(nrrVal - 100).toFixed(1)}%`,
      isPositive: nrrVal >= 100,
      subtext: nrrVal >= 100 ? 'Expansion outpaces churn' : 'Contraction under watch',
      icon: Repeat,
      iconBg: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-400',
    },
    {
      title: 'Customer Lifetime Value',
      shortLabel: 'LTV',
      value: formatValue(Number(ltvVal) || 0, currencyCode),
      trend: `${metrics.activeSubscriptions || 0} active`,
      isPositive: true,
      subtext: 'Average per active account',
      icon: Award,
      iconBg: 'bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-400',
    },
    {
      title: 'Recovered Revenue (MTD)',
      shortLabel: 'Dunning',
      value: formatValue(recoveredVal, currencyCode),
      trend: recoveredVal > 0 ? 'Protected' : '0 Pending',
      isPositive: true,
      subtext: 'From recovered checkouts & retries',
      icon: CheckCircle2,
      iconBg: 'bg-teal-50 text-teal-600 dark:bg-teal-950 dark:text-teal-400',
    },
    {
      title: 'Account Churn Rate',
      shortLabel: 'Churn',
      value: `${churnRateVal.toFixed(1)}%`,
      trend: churnRateVal <= 5 ? 'Healthy' : 'Elevated',
      isPositive: churnRateVal <= 5,
      subtext: `${metrics.activeSubscriptions || 0} active subscriptions`,
      icon: AlertTriangle,
      iconBg: 'bg-amber-50 text-amber-600 dark:bg-amber-950 dark:text-amber-400',
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3.5">
      {cards.map((card, idx) => {
        const Icon = card.icon;
        return (
          <div
            key={idx}
            className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200/80 dark:border-slate-700/80 shadow-xs hover:shadow-sm transition-all hover:border-slate-300 dark:hover:border-slate-600 flex flex-col justify-between"
          >
            <div className="flex items-center justify-between gap-2 mb-2">
              <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 truncate" title={card.title}>
                {card.title}
              </span>
              <div className={`p-1.5 rounded-lg ${card.iconBg}`}>
                <Icon className="w-3.5 h-3.5" />
              </div>
            </div>

            <div className="space-y-1">
              <h3 className="text-xl font-extrabold text-slate-900 dark:text-white tracking-tight truncate" title={card.value}>
                {card.value}
              </h3>
              <div className="flex items-center gap-1.5 text-[11px]">
                <span
                  className={`inline-flex items-center font-bold px-1.5 py-0.5 rounded-md ${
                    card.isPositive
                      ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400'
                      : 'bg-red-50 text-red-700 dark:bg-red-950/60 dark:text-red-400'
                  }`}
                >
                  {card.isPositive ? (
                    <ArrowUpRight className="w-3 h-3 mr-0.5" />
                  ) : (
                    <ArrowDownRight className="w-3 h-3 mr-0.5" />
                  )}
                  {card.trend}
                </span>
                <span className="text-slate-400 dark:text-slate-500 font-medium truncate">{card.subtext}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default DashboardMetrics;
